import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Card, CardHeader } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { employeeService } from '../services/employeeService';
import { Employee, UpdateEmployeeData } from '../types';

export function ProfilePage() {
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<UpdateEmployeeData>({});

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setIsLoading(true);
    try {
      const data = await employeeService.getMyProfile();
      setEmployee(data);
      // Only set form data if this is an employee profile (not admin account)
      if (data.firstName && data.lastName) {
        setFormData({
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone || '',
          address: data.address || '',
          city: data.city || '',
          state: data.state || '',
          pincode: data.pincode || '',
          bankName: data.bankName || '',
          accountNumber: data.accountNumber || '',
          ifscCode: data.ifscCode || '',
          panNumber: data.panNumber || '',
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updated = await employeeService.updateMyProfile(formData);
      setEmployee(updated);
      setIsEditing(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      // Error handled by interceptor
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: keyof UpdateEmployeeData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="spinner w-8 h-8 border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-8 text-gray-500">
        Failed to load profile. Please try again.
      </div>
    );
  }

  // Check if this is an admin account without employee profile
  const isAdminAccount = (employee as any).isAdmin || !employee.firstName;

  // Get initials for avatar
  const getInitials = () => {
    if (employee.firstName && employee.lastName) {
      return `${employee.firstName[0]}${employee.lastName[0]}`.toUpperCase();
    }
    if (employee.email) {
      return employee.email[0].toUpperCase();
    }
    return 'A';
  };

  return (
    <div className="space-y-6 animate-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
          <p className="text-gray-500">
            {isAdminAccount ? 'Admin account information' : 'Manage your personal and bank details'}
          </p>
        </div>
        {!isAdminAccount && (
          <Button
            variant={isEditing ? 'secondary' : 'primary'}
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? 'Cancel' : 'Edit Profile'}
          </Button>
        )}
      </div>

      {/* Profile Header */}
      <Card>
        <div className="flex items-center gap-6">
          <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center">
            <span className="text-3xl font-bold text-primary-600">
              {getInitials()}
            </span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {employee.firstName && employee.lastName 
                ? `${employee.firstName} ${employee.lastName}`
                : employee.email?.split('@')[0] || 'Admin User'}
            </h2>
            <p className="text-gray-500">
              {isAdminAccount 
                ? 'Administrator Account'
                : `${employee.designation || 'Employee'} • ${employee.department || 'General'}`}
            </p>
            {!isAdminAccount && (
              <div className="flex gap-4 mt-2 text-sm text-gray-600">
                <span className="badge badge-primary">{employee.employeeCode}</span>
                <span className="badge badge-gray">
                  {employee.gender === 'M' ? 'Male' : employee.gender === 'F' ? 'Female' : 'Other'}
                </span>
              </div>
            )}
            {isAdminAccount && (
              <div className="flex gap-4 mt-2 text-sm text-gray-600">
                <span className="badge badge-primary">ADMIN</span>
                <span className="text-gray-500">{(employee as any).message || 'No employee profile'}</span>
              </div>
            )}
          </div>
        </div>
      </Card>

      {isAdminAccount ? (
        <Card>
          <CardHeader title="Account Information" />
          <div className="space-y-4">
            <Input
              label="Email"
              value={employee.email || ''}
              disabled
              helperText="Admin account email"
            />
            <Input
              label="Role"
              value={(employee as any).role || 'ADMIN'}
              disabled
              helperText="Account role"
            />
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Admin Account:</strong> This is an administrative account without an employee profile. 
                You can manage all employees, run payroll, and access all system features.
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Personal Details */}
          <Card>
            <CardHeader title="Personal Details" />
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  value={formData.firstName || ''}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  disabled={!isEditing}
                />
                <Input
                  label="Last Name"
                  value={formData.lastName || ''}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  disabled={!isEditing}
                />
              </div>
              <Input
                label="Email"
                value={employee.email}
                disabled
                helperText="Email cannot be changed"
              />
              <Input
                label="Phone"
                value={formData.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
                disabled={!isEditing}
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Date of Birth"
                  value={employee.dateOfBirth ? new Date(employee.dateOfBirth).toLocaleDateString() : 'Not set'}
                  disabled
                />
                <Input
                  label="Join Date"
                  value={new Date(employee.joinDate).toLocaleDateString()}
                  disabled
                />
              </div>
            </div>
          </Card>

          {/* Address Details */}
          <Card>
            <CardHeader title="Address Details" />
            <div className="space-y-4">
              <Input
                label="Address"
                value={formData.address || ''}
                onChange={(e) => handleChange('address', e.target.value)}
                disabled={!isEditing}
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="City"
                  value={formData.city || ''}
                  onChange={(e) => handleChange('city', e.target.value)}
                  disabled={!isEditing}
                />
                <Input
                  label="State"
                  value={formData.state || ''}
                  onChange={(e) => handleChange('state', e.target.value)}
                  disabled={!isEditing}
                />
              </div>
              <Input
                label="Pincode"
                value={formData.pincode || ''}
                onChange={(e) => handleChange('pincode', e.target.value)}
                disabled={!isEditing}
              />
            </div>
          </Card>

          {/* Bank Details */}
          <Card>
            <CardHeader title="Bank Details" />
            <div className="space-y-4">
              <Input
                label="Bank Name"
                value={formData.bankName || ''}
                onChange={(e) => handleChange('bankName', e.target.value)}
                disabled={!isEditing}
              />
              <Input
                label="Account Number"
                value={formData.accountNumber || ''}
                onChange={(e) => handleChange('accountNumber', e.target.value)}
                disabled={!isEditing}
              />
              <Input
                label="IFSC Code"
                value={formData.ifscCode || ''}
                onChange={(e) => handleChange('ifscCode', e.target.value)}
                disabled={!isEditing}
              />
              <Input
                label="PAN Number"
                value={formData.panNumber || ''}
                onChange={(e) => handleChange('panNumber', e.target.value)}
                disabled={!isEditing}
              />
            </div>
          </Card>

          {/* Employment Details */}
          <Card>
            <CardHeader title="Employment Details" />
            <div className="space-y-4">
              <Input
                label="Department"
                value={employee.department || ''}
                disabled
                helperText="Contact HR to update"
              />
              <Input
                label="Designation"
                value={employee.designation || ''}
                disabled
                helperText="Contact HR to update"
              />
              <Input
                label="Base Salary"
                value={`₹${(employee.baseSalary || 0).toLocaleString()}`}
                disabled
                helperText="Contact HR to update"
              />
            </div>
          </Card>
        </div>
      )}

      {/* Save Button */}
      {!isAdminAccount && isEditing && (
        <div className="flex justify-end gap-4">
          <Button variant="secondary" onClick={() => setIsEditing(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} isLoading={isSaving}>
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
}
